// src/pages/BookingsListPage.tsx
import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from 'react';
import dayjs from 'dayjs';
import { Box, useTheme } from '@mui/material';
import {
  Add as AddIcon,
  RequestQuote as RequestQuoteIcon,
} from '@mui/icons-material';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { styled } from '@mui/material/styles';
import { bookingsAPI, BookingListItem } from '../services/api';
import BookingsList from '../components/lists/BookingsList/BookingsList';
import PageNumberText from '../components/common/PageNumberText';
import PaginationBottom from '../components/common/PaginationBottom';
import { FloatingAccessButton } from '../components/common/FloatingAccessButton';
import { useErrorHandler } from '../errors';
import { PageError } from '../components/common/PageError';
import { getApiErrorMessage } from '../utils/validationHelpers';
import LoadingSpinner from '../components/common/LoadingSpinner';
import VisuallyHiddenHeading from '../components/common/VisuallyHiddenHeading';
import {
  useBookingsFilter,
  BookingsFilterState,
} from '../hooks/useBookingsFilter';
import AlphabeticalFilter from '../components/AlphabeticalFilter';
import BookingsFilterDrawer from '../components/lists/BookingsList/BookingsFilterDrawer';
import { usePermissions } from '../hooks/usePermissions';
import { colors } from '../theme/theme';
import { getCustomIcon } from '../utils/customIcons';

const SORT_LABELS: Record<string, string> = {
  checkin: 'Check-in',
  checkout: 'Check-out',
  surname: 'Surname',
  firstname: 'First name',
  status: 'Status',
  created: 'Created',
};

const PageContainer = styled(Box)(() => ({
  width: '100%',
  margin: 0,
  padding: 0,
  display: 'flex',
  flexDirection: 'column',
}));

const ListContainer = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: '0',
  width: '100%',
  margin: 0,
  padding: 0,
});

const BottomRow = styled(Box)(({ theme }) => ({
  position: 'sticky',
  bottom: 0,
  backgroundColor: theme.palette.tertiary.main,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 30,
  boxShadow: colors.shadows.powerUp,
}));

const OnePageBottomText = styled(Box)(() => ({
  marginTop: '1rem',
  fontSize: '2rem',
}));

interface DrawerFilters {
  status?: string;
  startDate?: string;
  endDate?: string;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  isProspect?: boolean;
  isExpired?: boolean;
  isConverted?: boolean;
}

interface BookingsListPageProps {
  statusPreset?: string;
}

const BookingsListPage: React.FC<BookingsListPageProps> = ({
  statusPreset,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const [searchParams, setSearchParams] = useSearchParams();

  const { handleError } = useErrorHandler();
  const { canEditBookings } = usePermissions();

  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [hasPreviousPage, setHasPreviousPage] = useState(false);
  const [hasNextPage, setHasNextPage] = useState(false);

  const [bookings, setBookings] = useState<BookingListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // TODO: No route currently mounts BookingsListPage under /clients/:clientId/...
  // (the only live routes are /bookings and /quotes - see App.tsx), so this
  // is unreachable today. Left in place as scaffolding for a future
  // client-scoped bookings/quotations list; remove if that never lands.
  const clientIdFromPath = location.pathname.includes('/clients/')
    ? parseInt(location.pathname.split('/')[2])
    : null;

  const isQuotations = statusPreset === 'Quotation';
  // Quotes default to "most recently created first" (sales-pipeline view).
  // Bookings default to upcoming check-ins first (operational view).
  const defaultSortBy = isQuotations ? 'created' : 'checkin';
  // Both bookings and quotes default to "All clients" - drawer can
  // override to "Existing only" or "Prospects only".
  const defaultIsProspect: boolean | undefined = undefined;

  const {
    filterState,
    setFilter,
    setPageNumber,
    commitFilters,
    clearFilters,
    activeFilterCount,
    getFilterParams,
  } = useBookingsFilter(
    clientIdFromPath || undefined,
    statusPreset,
    defaultSortBy,
    defaultIsProspect
  );

  const [showMobileSearch, setShowMobileSearch] = useState(false);
  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);

  const [localTextFilter, setLocalTextFilter] = useState(
    filterState.searchText || ''
  );

  // Open-row state seeded from URL ?open=N. Shared with BookingsList in
  // controlled mode so the page can persist this to the URL as a
  // navigation-restorable marker.
  const [openItemId, setOpenItemIdState] = useState<number | null>(() => {
    const v = searchParams.get('open');
    return v ? parseInt(v) : null;
  });

  // One-shot: send openId to the API on the first load if URL had ?open=N,
  // so the server returns the page containing that booking.
  const pendingOpenId = useRef<number | null>(openItemId);
  // Suppress the next loadBookings effect run after we adopt the server's
  // pageNumber from an auto-jump (we already have the data).
  const skipNextLoad = useRef(false);
  // Flag set when the auto-jump load lands successfully, telling a follow-up
  // effect to scroll the open row into view once the list has rendered.
  const shouldScrollToOpen = useRef(false);

  // setSearchParams is NOT a stable reference - react-router rebuilds it
  // whenever the URL changes, and setPageNumber depends on it. We capture
  // setPageNumber in a ref so loadBookings's identity stays stable and URL
  // writes from pagination do not cascade into a re-fetch.
  const setPageNumberRef = useRef(setPageNumber);
  useEffect(() => {
    setPageNumberRef.current = setPageNumber;
  });

  // For the open-row marker we bypass react-router's setSearchParams and
  // write directly to history. setSearchParams triggers a location change
  // that ScrollResetWrapper observes (it watches location.search), which
  // would scroll the page to the top each time a row opens. replaceState
  // updates the URL silently so back-navigation still restores the marker
  // but no location-change listener fires.
  const setOpenItemId = useCallback((id: number | null) => {
    setOpenItemIdState(id);
    const url = new URL(window.location.href);
    if (id === null) {
      url.searchParams.delete('open');
    } else {
      url.searchParams.set('open', String(id));
    }
    window.history.replaceState(window.history.state, '', url.toString());
  }, []);

  useEffect(() => {
    setLocalTextFilter(filterState.searchText || '');
  }, [filterState.searchText]);

  const loadBookings = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const baseParams = getFilterParams();
      const params =
        pendingOpenId.current !== null
          ? { ...baseParams, openId: pendingOpenId.current }
          : baseParams;
      const result = await bookingsAPI.getAllPaginated(params);
      setBookings(result.data);
      setTotalPages(result.totalPages);
      setTotalCount(result.totalCount);
      setHasPreviousPage(result.hasPreviousPage);
      setHasNextPage(result.hasNextPage);

      if (pendingOpenId.current !== null) {
        const targetId = pendingOpenId.current;
        pendingOpenId.current = null;

        const found = result.data.some((b) => b.id === targetId);
        if (!found) {
          // Stale link or filtered out - silently drop the open marker.
          setOpenItemId(null);
        } else {
          shouldScrollToOpen.current = true;
          if (result.pageNumber !== (filterState.pageNumber ?? 1)) {
            // Server auto-jumped to a different page; adopt without re-fetching.
            skipNextLoad.current = true;
            setPageNumberRef.current(result.pageNumber);
          }
        }
      }
    } catch (err) {
      const errorMessage = getApiErrorMessage(err, 'LOAD_BOOKINGS');
      setError(errorMessage);
      handleError(err, { context: 'loading bookings', showToast: false });
    } finally {
      setLoading(false);
    }
  }, [handleError, getFilterParams, filterState.pageNumber, setOpenItemId]);

  useEffect(() => {
    if (skipNextLoad.current) {
      skipNextLoad.current = false;
      return;
    }
    loadBookings();
  }, [filterState, loadBookings]);

  // After an auto-jump load lands, scroll the open row into view. Runs only
  // once per flag flip, so user-driven open clicks during the session do not
  // re-scroll.
  useEffect(() => {
    if (
      !loading &&
      shouldScrollToOpen.current &&
      openItemId !== null &&
      bookings.some((b) => b.id === openItemId)
    ) {
      shouldScrollToOpen.current = false;
      const el = document.getElementById(`booking-row-${openItemId}`);
      el?.scrollIntoView({ behavior: 'instant', block: 'center' });
    }
  }, [loading, bookings, openItemId]);

  const handleFilterButtonClick = () => setFilterDrawerOpen(true);

  const handleApplyFilters = (drawerFilters: DrawerFilters) => {
    commitFilters({ ...filterState, ...drawerFilters, pageNumber: 1 });
    setFilterDrawerOpen(false);
  };

  const handleClearFilters = () => {
    setLocalTextFilter('');
    clearFilters();
  };

  const handleTextFilterChange = useCallback(
    (text: string) => {
      setLocalTextFilter(text);
      setFilter('searchText', text || undefined);
    },
    [setFilter]
  );

  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= totalPages) setPageNumber(newPage);
  };

  const handleEdit = (id: number) =>
    navigate(isQuotations ? `/quote/${id}/edit` : `/booking/${id}/edit`);
  const handleView = (id: number) =>
    navigate(isQuotations ? `/quote/${id}` : `/booking/${id}`);
  const handleDelete = (_id: number) => {};
  const handleCancel = (_id: number) => {};

  const filterDescription = useMemo(() => {
    const parts: string[] = [];
    if (localTextFilter) parts.push(`"${localTextFilter}"`);
    if (filterState.startDate)
      parts.push(`from ${dayjs(filterState.startDate).format('D MMM YYYY')}`);
    if (filterState.endDate)
      parts.push(`until ${dayjs(filterState.endDate).format('D MMM YYYY')}`);
    if (filterState.status && !isQuotations)
      parts.push(`"${filterState.status}"`);
    if (filterState.isProspect !== defaultIsProspect) {
      if (filterState.isProspect === true) parts.push('Prospects');
      if (filterState.isProspect === false) parts.push('Existing clients');
    }
    if (isQuotations && filterState.isConverted !== undefined) {
      parts.push(filterState.isConverted ? 'Converted' : 'Not converted');
    }
    if (isQuotations && filterState.isExpired !== undefined) {
      parts.push(filterState.isExpired ? 'Expired' : 'Not expired');
    }
    return parts.join(', ');
  }, [
    filterState.status,
    filterState.startDate,
    filterState.endDate,
    filterState.isProspect,
    filterState.isConverted,
    filterState.isExpired,
    localTextFilter,
    isQuotations,
    defaultIsProspect,
  ]);

  // Drawer chip description: omit sort when it matches the route default,
  // so the default-in-effect state reads as a clean list (no false "active" hints).
  const drawerFilterDescription = useMemo(() => {
    const parts: string[] = [];
    if (filterState.startDate)
      parts.push(`From ${dayjs(filterState.startDate).format('D MMM YYYY')}`);
    if (filterState.endDate)
      parts.push(`Until ${dayjs(filterState.endDate).format('D MMM YYYY')}`);
    if (filterState.status && !isQuotations) parts.push(filterState.status);
    if (filterState.isProspect !== defaultIsProspect) {
      if (filterState.isProspect === true) parts.push('Prospects');
      if (filterState.isProspect === false) parts.push('Existing');
    }
    if (isQuotations && filterState.isConverted !== undefined) {
      parts.push(filterState.isConverted ? 'Converted' : 'Not converted');
    }
    if (isQuotations && filterState.isExpired !== undefined) {
      parts.push(filterState.isExpired ? 'Expired' : 'Not expired');
    }
    if (filterState.sortBy && filterState.sortBy !== defaultSortBy) {
      const label = SORT_LABELS[filterState.sortBy] ?? filterState.sortBy;
      parts.push(`${label} ${filterState.sortOrder === 'asc' ? '↑' : '↓'}`);
    }
    return parts.join(', ');
  }, [
    filterState.status,
    filterState.startDate,
    filterState.endDate,
    filterState.sortBy,
    filterState.sortOrder,
    filterState.isProspect,
    filterState.isConverted,
    filterState.isExpired,
    isQuotations,
    defaultSortBy,
    defaultIsProspect,
  ]);

  const contextType: 'bookings' | 'quotations' = isQuotations
    ? 'quotations'
    : 'bookings';
  const beingsLabel = isQuotations ? 'quotations' : 'bookings';
  const pageTitle = isQuotations ? 'Quotes' : 'Bookings';

  if (loading) {
    return (
      <PageContainer>
        <VisuallyHiddenHeading title={pageTitle} />
        <LoadingSpinner message={`Loading ${beingsLabel}...`} size={80} />
      </PageContainer>
    );
  }

  if (error) {
    return (
      <PageContainer>
        <VisuallyHiddenHeading title={pageTitle} />
        <PageError message={error} onRetry={loadBookings} />
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <VisuallyHiddenHeading title={pageTitle} />
      <AlphabeticalFilter
        activeFilter={null}
        onFilterChange={() => {}}
        textFilter={localTextFilter}
        onTextFilterChange={handleTextFilterChange}
        itemCount={totalCount}
        contextType={contextType}
        showMobileSearch={showMobileSearch}
        setShowMobileSearch={setShowMobileSearch}
        showMobileAlphabetMenu={false}
        setShowMobileAlphabetMenu={() => {}}
        showAlphabetGrid={false}
        showMobileAlphabetButton={false}
        activeFilterCount={activeFilterCount}
        onFilterButtonClick={handleFilterButtonClick}
        pageNumber={filterState.pageNumber || 1}
        pageSize={filterState.pageSize || 50}
        totalCount={totalCount}
        hasNextPage={hasNextPage}
        hasPreviousPage={hasPreviousPage}
        onPageChange={handlePageChange}
        filterDescription={drawerFilterDescription}
      />

      <ListContainer>
        <BookingsList
          items={bookings}
          type="bookings"
          onEdit={handleEdit}
          onView={handleView}
          onDelete={handleDelete}
          onCancel={handleCancel}
          filterDrawerOpen={filterDrawerOpen}
          setFilterDrawerOpen={setFilterDrawerOpen}
          openItemId={openItemId}
          onOpenChange={setOpenItemId}
        />
      </ListContainer>

      <PageNumberText current={filterState.pageNumber} total={totalPages} />
      {totalPages > 1 ? (
        <BottomRow>
          <PaginationBottom
            pageNumber={filterState.pageNumber || 1}
            totalPages={totalPages}
            totalCount={totalCount}
            pageSize={filterState.pageSize || 50}
            beingType={contextType}
            activeFilter={null}
            textFilter={localTextFilter}
            hasNextPage={hasNextPage}
            hasPreviousPage={hasPreviousPage}
            onPageChange={handlePageChange}
          />
        </BottomRow>
      ) : (
        <Box
          sx={{
            height: '5rem',
            textAlign: 'center',
            borderTop: '2px solid',
            borderTopColor: theme.palette.divider,
            boxShadow: colors.shadows.powerInset,
          }}
        >
          <OnePageBottomText>
            {totalCount} {beingsLabel} found
            {filterDescription ? ` for ${filterDescription}` : ''}
          </OnePageBottomText>
        </Box>
      )}

      {statusPreset === 'Quotation' && canEditBookings && !filterDrawerOpen && (
        <FloatingAccessButton
          icon={<AddIcon />}
          hoverIcon={getCustomIcon('quotation', '4rem')}
          onClick={() => navigate('/quote/new')}
          aria-label="Add new quote"
          iconScale={1.5}
          backgroundColor={theme.palette.secondary.main}
          hoverBackgroundColor={theme.palette.secondary.dark}
          bottom={totalPages > 1 ? '7rem' : '2rem'}
          right="5%"
          sx={{
            border: '2px solid #fff',
            '@media (max-width: 600px)': {
              bottom: totalPages > 1 ? '8.5rem' : '1rem',
              right: '1rem',
            },
            '@media (min-width: 601px)': {
              width: '75px',
              height: '75px',
              minWidth: '75px',
              minHeight: '75px',
            },
          }}
        />
      )}

      <BookingsFilterDrawer
        open={filterDrawerOpen}
        onClose={() => setFilterDrawerOpen(false)}
        filterState={filterState}
        onApply={handleApplyFilters}
        onClear={handleClearFilters}
        isQuotations={isQuotations}
      />
    </PageContainer>
  );
};

export default BookingsListPage;
